import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  Image,
  Menu,
  LogIn,
  LogOut,
  ShieldAlert,
  ScanText,
  SquareMousePointer,
  Sheet,
  BookUser,
  Network,
  FileUser,
  ChevronRight,
  Settings,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import logo from "../Images/LF.png";
import logo_expanded from "../Images/LF_expanded.png";

// Color palette
const COLORS = {
  green: "#FAFAFA",
  green2: "#9aa390ff",
  white: "#221b8dff",
};

const NavBar = ({ onNavClick, activeTab: propActiveTab, onHover }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLocked, setIsLocked] = useState(false); // State for locking the navbar
  const navigate = useNavigate();
  const location = useLocation(); // Track current location
  const { isAuthenticated, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(propActiveTab);

  // const navItems = [
  //   { id: "home", icon: Home, label: "Home", path: "/" },
  //   {
  //     id: "bbox",
  //     icon: SquareMousePointer,
  //     label: "Focus AI",
  //     path: "/bbox",
  //     requiresAuth: true,
  //   },
  //   {
  //     id: "OCR",
  //     icon: ScanText,
  //     label: "TextIQ",
  //     path: "/ocr",
  //     requiresAuth: true,
  //   },
  //   {
  //     id: "CSV",
  //     icon: Sheet,
  //     label: "IndexGenius",
  //     path: "/csv",
  //     requiresAuth: true,
  //   },
  //   {
  //     id: "admin",
  //     icon: ShieldAlert,
  //     label: "Admin",
  //     path: "/admin",
  //     requiresAuth: true,
  //   },
  //   // Add login/logout as a nav item
  //   {
  //     id: isAuthenticated() ? "logout" : "login",
  //     icon: isAuthenticated() ? LogOut : LogIn,
  //     label: isAuthenticated() ? "Logout" : "Login",
  //     path: isAuthenticated() ? "/" : "/login",
  //   },
  // ];

  const navItems = [
    { id: "home", icon: Home, label: "Home", path: "/" },
    // {
    //   id: "bbox",
    //   icon: SquareMousePointer,
    //   label: "Focus AI",
    //   path: "/bbox",
    //   requiresAuth: true,
    // },
    {
      id: "Settings",
      icon: Settings,
      label: "Settings",
      path: "/settings",
    },
    // {
    //   id: "admin",
    //   icon: ShieldAlert,
    //   label: "Admin",
    //   path: "/admin",
    //   requiresAuth: true,
    // },
    // Add login/logout as a nav item
    {
      id: isAuthenticated() ? "logout" : "login",
      icon: isAuthenticated() ? LogOut : LogIn,
      label: isAuthenticated() ? "Logout" : "Login",
      path: isAuthenticated() ? "/" : "/login",
    },
  ];

  // Effect to sync active tab with current route
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingItem = navItems.find((item) => item.path === currentPath);
    if (matchingItem) {
      setActiveTab(matchingItem.id);
      if (onNavClick) {
        onNavClick(matchingItem.id);
      }
    }
  }, [location.pathname, navItems, onNavClick]);

  // Effect to sync with prop changes
  useEffect(() => {
    if (propActiveTab) {
      setActiveTab(propActiveTab);
    }
  }, [propActiveTab]);

  useEffect(() => {
    // Pass the current expanded state to the parent component
    // Only if not locked in collapsed state
    onHover(isLocked ? false : isHovered);
  }, [isHovered, isLocked, onHover]);

  const handleNavClick = (tabId) => {
    const item = navItems.find((item) => item.id === tabId);

    if (tabId === "logout") {
      logout();
      navigate("/login");
    } else if (tabId === "login") {
      navigate("/login");
    } else if (item) {
      setActiveTab(tabId);
      onNavClick(tabId);
      navigate(item.path);
    }
  };

  // Handle mouse enter only if navbar is not locked
  const handleMouseEnter = () => {
    if (!isLocked) {
      setIsHovered(true);
    }
  };

  // Handle mouse leave only if navbar is not locked
  const handleMouseLeave = () => {
    if (!isLocked) {
      setIsHovered(false);
    }
  };

  // Toggle the locked state - always lock in collapsed state
  const toggleLock = () => {
    setIsLocked(!isLocked);
    // When locking, ensure navbar is collapsed
    if (!isLocked) {
      setIsHovered(false);
    }
  };

  return (
    <motion.nav
      initial={false}
      animate={{
        width: isHovered ? 240 : 70,
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed left-0 top-0 h-full shadow-xl z-50"
      style={{
        backgroundColor: COLORS.green,
        color: COLORS.white,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo */}
      <div
        className="py-4 flex justify-between items-center border-b border-opacity-10"
        style={{ borderColor: COLORS.green2 }}
      >
        <div className="flex justify-center items-center flex-grow">
          <motion.div
            animate={{
              opacity: isHovered ? 1 : 0,
              scale: isHovered ? 1 : 0.95,
              display: isHovered ? "flex" : "none",
            }}
            transition={{ duration: 0.2 }}
            className="flex items-center"
          >
           <img 
  src={logo_expanded} 
  alt="ACTIGEN Logo" 
  className="max-w-[140px] max-h-[44px] mr-2" 
  style={{ objectFit: "contain" }}
/>
 
          </motion.div>
          <motion.div
            animate={{
              opacity: isHovered ? 0 : 1,
              scale: isHovered ? 0.95 : 1,
              display: isHovered ? "none" : "flex",
            }}
            transition={{ duration: 0.2 }}
            className="flex justify-center items-center w-full"
          >
            <img
              src={logo}
              alt="ACTIGEN Logo"
              className="w-10 h-10"
              style={{ objectFit: "contain" }}
            />
          </motion.div>
        </div>

   
        <motion.button
          onClick={toggleLock}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={{
            rotate: isLocked ? 180 : 0,
            opacity: isHovered ? 1 : 0.7,
          }}
          transition={{ duration: 0.3 }}
          className="absolute top-14 right-1"
          style={{
            color: isLocked ? COLORS.green2 : COLORS.white,
            zIndex: 10,
          }}
          title={
            isLocked
              ? "Unlock navigation bar"
              : "Lock navigation bar in collapsed state"
          }
        >
          <ChevronRight className="w-4 h-4" />
        </motion.button>
      </div>

      {/* Navigation Items */}
      <div className="py-6 px-2 flex flex-col h-[calc(100%-80px)]">
        {/* Main navigation items */}
        <div className="flex-grow">
          {navItems.map(
            (item, index) =>
              (!item.requiresAuth || isAuthenticated()) && (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center px-4 py-4 my-2 rounded-xl transition-all"
                  style={{
                    backgroundColor:
                      activeTab === item.id ? COLORS.white : "transparent",
                    color: activeTab === item.id ? COLORS.green : COLORS.white,
                  }}
                >
                  <item.icon className="w-6 h-6 min-w-[24px]" />
                  <motion.span
                    animate={{
                      opacity: isHovered ? 1 : 0,
                      x: isHovered ? 0 : -20,
                    }}
                    transition={{ duration: 0.2 }}
                    className="ml-4 whitespace-nowrap"
                  >
                    {item.label}
                  </motion.span>

                  {activeTab === item.id && !isHovered && (
                    <div
                      className="absolute left-0 w-1 h-8 rounded-r-full"
                      style={{ backgroundColor: COLORS.green2 }}
                    ></div>
                  )}
                </motion.button>
              )
          )}
        </div>
      </div>
    </motion.nav>
  );
};

export default NavBar;
